import React, { useState, useEffect } from 'react';
import {
    Modal,
    Card,
    Avatar,
    Typography,
    Button,
    Space,
    Badge,
    Tag,
    Divider
} from 'antd';
import {
    UserOutlined,
    CalendarOutlined,
    EnvironmentOutlined,
    PhoneOutlined,
    CheckOutlined,
    CloseOutlined,
    SoundOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import pusherService from '../../services/pusher';
import soundNotificationService from '../../services/soundNotification';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

function LeadNotificationPopup() {
    const { user } = useAuth();
    const [visible, setVisible] = useState(false);
    const [currentLead, setCurrentLead] = useState(null);
    const [soundEnabled, setSoundEnabled] = useState(true);

    useEffect(() => {
        if (!user) return;

        const setupNotifications = async () => {
            try {
                // Listen for new leads if user is vendor or admin
                if (user.user_type === 'vendor' || user.user_type === 'admin') {
                    // Use user-specific channel instead of 'new-leads'
                    const leadChannel = await pusherService.subscribeToChannel(`user-${user.id}`);
                    if (leadChannel) {
                        leadChannel.bind('new-lead', (data) => {
                            console.log('New lead notification received:', data);
                            showLeadPopup(data.lead);
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to setup lead notifications:', error);
            }
        };

        setupNotifications();
    }, [user]);

    useEffect(() => {
        // Initialize sound settings
        setSoundEnabled(soundNotificationService.isSoundEnabled());
    }, []);

    useEffect(() => {
        console.log('Popup visible state changed to:', visible);
        console.log('Current lead:', currentLead);
    }, [visible, currentLead]);

    const showLeadPopup = (lead) => {
        console.log('Showing lead popup for lead:', lead);
        setCurrentLead(lead);
        setVisible(true);
        
        // Play siren sound
        if (soundEnabled) {
            soundNotificationService.playSirenSound();
        }
    };

    const handleAccept = async () => {
        if (!currentLead) return;
        try {
            console.log('Accepting lead:', currentLead);
            console.log('Lead ID:', currentLead.id);
            console.log('Lead vendor_id:', currentLead.vendor_id);
            console.log('Current user:', user);
            console.log('Current popup visible state:', visible);
            
            const response = await api.updateLead(currentLead.id, { status: 'accepted' });
            console.log('Lead acceptance response:', response);
            console.log('Response success:', response?.success);
            console.log('Response data:', response?.data);
            
            // Check for success in multiple possible response formats
            const isSuccess = response?.success === true || 
                            response?.data?.success === true || 
                            (response?.status >= 200 && response?.status < 300);
            
            console.log('Is success:', isSuccess);
            
            if (isSuccess) {
                console.log('Lead accepted successfully, closing popup');
                setVisible(false);
                setCurrentLead(null);
                console.log('Popup should be closed now');
            } else {
                console.error('Failed to accept lead:', response?.message || response?.data?.message);
                console.error('Response errors:', response?.errors || response?.data?.errors);
                // Still close the popup even if API fails
                console.log('API failed but closing popup anyway');
                setVisible(false);
                setCurrentLead(null);
            }
            
            // Force close after 2 seconds as fallback
            setTimeout(() => {
                if (visible) {
                    console.log('Force closing popup after timeout');
                    setVisible(false);
                    setCurrentLead(null);
                }
            }, 2000);
            
        } catch (error) {
            console.error('Error accepting lead:', error);
            console.error('Error details:', error.response?.data);
            console.error('Error status:', error.response?.status);
            // Close popup even on error
            console.log('Error occurred but closing popup anyway');
            setVisible(false);
            setCurrentLead(null);
            
            // Force close after 2 seconds as fallback
            setTimeout(() => {
                if (visible) {
                    console.log('Force closing popup after error timeout');
                    setVisible(false);
                    setCurrentLead(null);
                }
            }, 2000);
        }
    };

    const handleReject = async () => {
        if (!currentLead) return;
        try {
            console.log('Rejecting lead:', currentLead.id);
            const response = await api.updateLead(currentLead.id, { status: 'rejected' });
            console.log('Lead rejection response:', response);
            
            if (response.success) {
                setVisible(false);
                setCurrentLead(null);
            } else {
                console.error('Failed to reject lead:', response.message);
            }
        } catch (error) {
            console.error('Error rejecting lead:', error);
        }
    };

    const handleClose = () => {
        console.log('handleClose called, current visible state:', visible);
        setVisible(false);
        setCurrentLead(null);
        console.log('Popup should be closed after handleClose');
    };

    const handleViewDetails = () => {
        // Navigate to lead details or chat
        window.location.href = `/chat/${currentLead.id}`;
        setVisible(false);
        setCurrentLead(null);
    };

    const toggleSound = () => {
        const newState = !soundEnabled;
        setSoundEnabled(newState);
        soundNotificationService.setEnabled(newState);
    };

    const formatTime = (timestamp) => {
        return dayjs(timestamp).format('HH:mm');
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            quoted: 'blue',
            accepted: 'green',
            rejected: 'red',
            completed: 'green'
        };
        return colors[status] || 'default';
    };

    if (!currentLead) return null;

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            footer={null}
            width={500}
            centered
            destroyOnClose={true}
            className="lead-notification-modal"
            style={{
                top: '20px'
            }}
        >
            <Card
                className="border-0 shadow-2xl"
                style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '16px'
                }}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                        <Title level={4} className="!mb-0 !text-white">
                            🚨 New Lead Alert!
                        </Title>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                            type="text"
                            icon={<SoundOutlined />}
                            onClick={toggleSound}
                            className={`text-white hover:bg-white hover:bg-opacity-20 ${
                                soundEnabled ? 'text-green-300' : 'text-gray-400'
                            }`}
                            size="small"
                        />
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={handleClose}
                            className="text-white hover:bg-white hover:bg-opacity-20"
                            size="small"
                        />
                    </div>
                </div>

                {/* Lead Details */}
                <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-4">
                    <div className="flex items-start space-x-3">
                        <Avatar 
                            size={48} 
                            icon={<UserOutlined />}
                            src={currentLead.user?.avatar}
                            className="ring-2 ring-white ring-opacity-30"
                        />
                        <div className="flex-1">
                            <Title level={5} className="!mb-1 !text-white">
                                {currentLead.user?.name || 'Unknown Customer'}
                            </Title>
                            <Text className="text-blue-100 text-sm">
                                {currentLead.event_type} • {currentLead.event_date}
                            </Text>
                        </div>
                        <Tag 
                            color={getStatusColor(currentLead.status)}
                            className="rounded-full"
                        >
                            {currentLead.status?.toUpperCase()}
                        </Tag>
                    </div>

                    <Divider className="my-3 border-white border-opacity-20" />

                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center space-x-2 text-blue-100">
                            <CalendarOutlined />
                            <Text className="text-white">{currentLead.event_date} at {currentLead.event_time}</Text>
                        </div>
                        <div className="flex items-center space-x-2 text-blue-100">
                            <UserOutlined />
                            <Text className="text-white">{currentLead.guest_count} guests</Text>
                        </div>
                        <div className="flex items-center space-x-2 text-blue-100 col-span-2">
                            <EnvironmentOutlined />
                            <Text className="text-white truncate">{currentLead.venue}</Text>
                        </div>
                        <div className="flex items-center space-x-2 text-blue-100 col-span-2">
                            <PhoneOutlined />
                            <Text className="text-white">{currentLead.contact_phone}</Text>
                        </div>
                    </div>

                    {currentLead.special_requirements && (
                        <div className="mt-3 p-2 bg-white bg-opacity-10 rounded">
                            <Text className="text-white text-xs">
                                <strong>Special Requirements:</strong> {currentLead.special_requirements}
                            </Text>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleAccept}
                        className="flex-1 bg-green-500 border-green-500 hover:bg-green-600 rounded-full font-medium"
                    >
                        Accept Lead
                    </Button>
                    <Button
                        onClick={handleViewDetails}
                        className="flex-1 bg-white text-gray-800 hover:bg-gray-100 rounded-full font-medium"
                    >
                        View Details
                    </Button>
                </div>

                {/* Timestamp */}
                <div className="text-center mt-3">
                    <Text className="text-blue-200 text-xs">
                        Received at {formatTime(new Date())}
                    </Text>
                </div>
            </Card>
        </Modal>
    );
}

export default LeadNotificationPopup;
